import { useState } from "react";
import { Button } from "@/shared/components/button";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import {
  claimReviewItemApi,
  getClinicianReviewItemApi,
  previewQueueItemApi,
  releaseReviewItemApi,
  type ClinicianReviewItem,
  type QueueItem,
  type QueueItemPreview,
} from "@/portals/clinician/api/reviewQueueApi";
import { AttachedViaTag, ReviewStatusBadge } from "./reviewBadges";
import { DecisionPanel } from "./DecisionPanel";
import { useClinicianReviewLive } from "../hooks/useClinicianReviewLive";
import { clinicianQueryKeys } from "@/portals/clinician/api/clinicianQueryKeys";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { PatientIdentity, ReviewItemLayout, ReviewItemScreenMessage, ViewOnlyActionPanel } from "./ReviewItemLayout";
import { ReviewHistory } from "./ReviewHistory";

export const MESSAGES = {
  released: "Item released to the shared queue.",
  notReleasable: "This item can't be released right now.",
  notFound: "This item isn't available.",
  loadFailed: "Couldn't load this item. Please try again.",
  noAnswer: "No answer yet",
  claimedElsewhere: "Another clinician already claimed this item.",
  previewIdentity: "Patient details are shown after you claim this item.",
  previewNote: "View only — claim this item to record a decision or ask the patient a question.",
};

type ScreenItem = ClinicianReviewItem | QueueItemPreview;

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; item: ScreenItem }
  | { kind: "not-found" }
  | { kind: "claimed-elsewhere" }
  | { kind: "load-failed" };

const isHeld = (item: ScreenItem): item is ClinicianReviewItem => "patient" in item;

interface ClinicianReviewItemViewProps {
  itemId: string;
  /** Where Back goes, for its label ("My Items" / "Review Queue"). */
  backLabel?: string;
  onBack: () => void;
  /** After a successful release — back to the dashboard. */
  onReleased: () => void;
  /** After a resolving decision — back to the dashboard (the item leaves My items). */
  onDecided?: () => void;
  /** "preview": a shared-queue item looked at before claiming — read-only,
   *  no patient identity, a Claim button in place of the decision panel. */
  mode?: "held" | "preview";
  /** Preview only: after a successful claim (the item is now held). */
  onClaimed?: (id: string) => void;
}

export function ClinicianReviewItemView({ itemId, backLabel = "Review Queue", onBack, onReleased, onDecided, mode = "held", onClaimed }: ClinicianReviewItemViewProps) {
  const preview = mode === "preview";
  const [releasing, setReleasing] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [afterAction, setAfterAction] = useState<"not-found" | "claimed-elsewhere" | null>(null);

  // Cached per item: reopening an item shows it at once, then refreshes it quietly.
  const query = useCachedQuery<ScreenItem, { code?: string; message?: string } | null>(
    preview ? clinicianQueryKeys.queuePreview(itemId) : clinicianQueryKeys.reviewItem(itemId),
    () => (preview ? previewQueueItemApi(itemId) : getClinicianReviewItemApi(itemId)),
  );
  const { cache } = query;
  const load = () => void query.refresh();
  // Live: a patient answering (or resolving it with "I no longer take this")
  // arrives as a notification / queue event — refetch so the screen is current.
  useClinicianReviewLive(load, load);
  const state: ScreenState = afterAction
    ? { kind: afterAction }
    : query.data
      ? { kind: "ready", item: query.data }
      : query.loading || !query.error
        ? { kind: "loading" }
        : {
            kind: query.error.code === "REVIEW_ITEM_NOT_FOUND"
              ? "not-found"
              : query.error.code === "ITEM_ALREADY_CLAIMED"
                ? "claimed-elsewhere"
                : "load-failed",
          };
  const setState = (next: { kind: "ready"; item: ClinicianReviewItem } | { kind: "not-found" }) => {
    if (next.kind === "ready") query.mutate(() => next.item);
    else setAfterAction("not-found");
  };
  // After a release or a resolving decision the item has left My items — drop it
  // there right away (no stale row when the dashboard reappears) and forget this
  // item's cached copy, so it's reloaded fresh if opened again.
  const forgetItem = () => {
    const mine = cache.get<{ id: string }[]>(clinicianQueryKeys.mine);
    if (mine) cache.set(clinicianQueryKeys.mine, mine.filter((i) => i.id !== itemId));
    cache.invalidate(clinicianQueryKeys.reviewItem(itemId));
  };

  const item = state.kind === "ready" ? state.item : null;

  // Preview → claim: on success the item is held, so seed its full view in the
  // cache (opens instantly), drop it from the shared queue, and hand over.
  const claim = async () => {
    setClaiming(true);
    const { data, error } = await claimReviewItemApi(itemId);
    setClaiming(false);
    if (data) {
      cache.set(clinicianQueryKeys.reviewItem(itemId), data);
      const queue = cache.get<QueueItem[]>(clinicianQueryKeys.queue);
      if (queue) cache.set(clinicianQueryKeys.queue, queue.filter((i) => i.id !== itemId));
      cache.invalidate(clinicianQueryKeys.queuePreview(itemId));
      onClaimed?.(itemId);
    } else if (error?.code === "ITEM_ALREADY_CLAIMED") {
      toastError(MESSAGES.claimedElsewhere);
      setAfterAction("claimed-elsewhere");
    } else if (error?.code === "REVIEW_ITEM_NOT_FOUND") {
      setAfterAction("not-found");
    } else {
      toastError(error?.message ?? "Unable to claim this item.");
    }
  };

  const release = async () => {
    if (!item) return;
    setReleasing(true);
    const { error } = await releaseReviewItemApi(item.id);
    setReleasing(false);
    if (!error) {
      toastSuccess(MESSAGES.released);
      forgetItem();
      onReleased();
    } else if (error.code === "ITEM_NOT_RELEASABLE") {
      toastError(MESSAGES.notReleasable);
      load();
    } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
      setState({ kind: "not-found" });
    } else {
      toastError(error.message);
    }
  };

  if (state.kind !== "ready") {
    return (
      <ReviewItemScreenMessage
        loading={state.kind === "loading"}
        icon={state.kind === "not-found" ? "lucide:file-x" : state.kind === "claimed-elsewhere" ? "lucide:user-check" : "lucide:wifi-off"}
        message={state.kind === "not-found" ? MESSAGES.notFound : state.kind === "claimed-elsewhere" ? MESSAGES.claimedElsewhere : MESSAGES.loadFailed}
        backLabel={backLabel}
        onBack={onBack}
      />
    );
  }

  const it = state.item;

  return (
    <ReviewItemLayout
      item={it}
      backLabel={backLabel}
      onBack={onBack}
      identity={
        isHeld(it) ? (
          <PatientIdentity patient={it.patient} />
        ) : (
          <div>
            <p className="text-lg font-extrabold text-[#0A2947]">Patient hidden</p>
            <p className="text-xs text-[#6B7280]">{MESSAGES.previewIdentity}</p>
          </div>
        )
      }
      badges={
        <>
          <ReviewStatusBadge status={it.itemStatus} />
          {isHeld(it) && <AttachedViaTag via={it.attachedVia} />}
        </>
      }
      // Patient Conversation: questions, answers and the decision, in order — held items only.
      renderConversation={isHeld(it) ? (isPair) => <ReviewHistory item={it} isPair={isPair} /> : undefined}
      aside={
        // Decision panel (Module 5) + release — or, in preview, only Claim.
        !isHeld(it) ? (
          <ViewOnlyActionPanel
            testId="preview-claim-panel"
            note={MESSAGES.previewNote}
            actionLabel="Claim this item"
            busy={claiming}
            onAction={() => void claim()}
          />
        ) : (
          <DecisionPanel
            key={`${it.id}:${it.itemStatus}`}
            item={it}
            onResolved={() => {
              forgetItem();
              (onDecided ?? onBack)();
            }}
            onItemChanged={(updated) => (updated ? setState({ kind: "ready", item: updated }) : load())}
            onNotFound={() => setState({ kind: "not-found" })}
            // Same line as "Record decision", inside the panel.
            secondaryAction={it.itemStatus === "IN_CLINICIAN_REVIEW" ? (
              <Button
                variant="outline"
                busy={releasing}
                onClick={() => void release()}
                className="!h-10 !w-full !rounded-lg !border-[#E5E7EB] !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
              >
                Release to queue
              </Button>
            ) : undefined}
          />
        )
      }
    />
  );
}
